import styled from 'styled-components/native';
import {
  border,
  BorderProps,
  SpaceProps,
  HeightProps,
  fontSize,
  FontSizeProps,
  color,
  ColorProps,
} from 'styled-system';

import { TextInputProps as NativeTextInputProps, Platform } from 'react-native';

import { Colors } from 'theme/colors';
import { FontFamily, FontWeight } from 'theme/fonts';
import { height, space } from 'theme/responsive';

/**
 * Returns border variants for the border
 *
 * @param props - TextInput props
 * @returns Border color
 */
function borderVariant(props: TextInputContainerProps) {
  const { focused, editable, accentColor } = props;

  if (focused && editable !== false) {
    return accentColor ?? Colors.green;
  } else if (!editable) {
    return Colors.grey;
  }

  return Colors.steel;
}
export const TextInput = styled.TextInput<TextInputProps>`
  flex: 1;
  outline-width: 0px;
  outline-style: none;
  color: ${({ editable }) => (editable === false ? Colors.steel : Colors.black)};
  font-family: ${Platform.OS === 'web' ? 'Roboto' : FontFamily.Light};
  font-weight: ${FontWeight.Light};
  ${fontSize}
  ${border}
  ${space}
  ${height}
  ${color}
`;

export const InputContainer = styled.View<TextInputContainerProps>`
  position: relative;
  flex-direction: row;
  align-items: center;
  border-radius: 7px;
  border-width: 1px;
  border-color: ${borderVariant};
  border-top-right-radius: ${({ errorMessage }) => (errorMessage ? '0px' : '7px')};
  border-top-left-radius: ${({ errorMessage }) => (errorMessage ? '0px' : '7px')};
  ${border}
  ${space}
  ${height}
  ${color}
`;

export const SecureSwitch = styled.TouchableOpacity<TextInputProps>`
  position: absolute;
  bottom: 0px;
  height: ${({ errorMessage }) => (errorMessage ? '45px' : '100%')};
  justify-content: center;
  padding-right: 16px;
  right: 0;
  display: flex;
`;

export const ClearText = styled.TouchableOpacity<TextInputProps>`
  position: absolute;
  bottom: 0px;
  height: ${({ errorMessage }) => (errorMessage ? '45px' : '100%')};
  justify-content: center;
  display: ${({ clearable }) => (clearable ? '' : 'none')};
  padding-right: ${({ clearable, secureTextEntry }) =>
    secureTextEntry && clearable ? '45px' : '16px'};
  right: 0;
`;

export const InlineErrorContainer = styled.View<TextInputProps & { isVisible?: boolean }>`
  align-items: center;
  display: flex;
  background: ${Colors.lightGrey};
  border-top-right-radius: 7px;
  border-top-left-radius: 7px;
  flex-direction: row;
  padding: 8px;
  width: 100%;
  position: ${({ position }) => (position ? position : 'absolute')};
  top: -33px;
  left: 0px;
  right: 0px;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: ${({ isVisible }) => (isVisible ? 'translateY(0px)' : 'translateY(33px)')};
`;

export const InlineError = styled.Text<TextInputProps>`
  font-family: Roboto;
  font-size: 14px;
  font-weight: 300;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.29em;
  letter-spacing: 0.28px;
  margin-left: 8px;
  color: ${Colors.tundora};
`;

/* Type definitions */
export interface FocusableProps {
  /** Indicator for whether a field is focused */
  focused?: boolean;
}

export interface EditableProps {
  /** Indicator for whether a field is editable */
  editable?: boolean;
}
export interface AccentColorProps {
  /** Color with which to override the accent color */
  accentColor?: string;
}

export interface ClearableProps {
  /** Indicator for whether a field is clearable */
  clearable?: boolean;
}

export interface SecureTextEntryProps {
  /** Indicator for whether a field is secured(password) text */
  secureTextEntry?: boolean;
}

export interface ErrorProps {
  /** positioning on the error message */
  position?: string;
  /** An array of errors */
  error?: string[];
  /** The Error message to show on a field */
  errorMessage?: string;
}

export interface IconProps {
  /** The custom icon to show on left of a field */
  icon?: string;
}
type TextInputContainerProps = BorderProps &
  ErrorProps &
  ColorProps &
  SpaceProps &
  HeightProps &
  FocusableProps &
  EditableProps &
  AccentColorProps;

export type TextInputProps = BorderProps &
  ColorProps &
  IconProps &
  ErrorProps &
  SpaceProps &
  HeightProps &
  FocusableProps &
  ClearableProps &
  SecureTextEntryProps &
  EditableProps &
  FontSizeProps &
  NativeTextInputProps;
